/**
 * Given an array of raw bytes, ensure that sample rate is at maximum the target
 * sample rate
 * @param srcFrame input
 * @param srcRate  sample rate of input
 * @param dstRate target sample rate
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common/RiffPcmEncoder.ts#L45
 */
export const downSample = (
  srcFrame: Float32Array,
  srcRate: number,
  dstRate = 16000
): Float32Array => {
  if (dstRate === srcRate || dstRate > srcRate) {
    return srcFrame;
  }

  const ratio = srcRate / dstRate;
  const dstLength = Math.round(srcFrame.length / ratio);
  const dstFrame = new Float32Array(dstLength);
  let srcOffset = 0;
  let dstOffset = 0;
  while (dstOffset < dstLength) {
    const nextSrcOffset = Math.round((dstOffset + 1) * ratio);
    let accum = 0;
    let count = 0;
    while (srcOffset < nextSrcOffset && srcOffset < srcFrame.length) {
      accum += srcFrame[srcOffset++];
      count++;
    }
    dstFrame[dstOffset++] = accum / count;
  }

  return dstFrame;
};

/**
 * Convert raw audio data to 16 bits (for having 2 bytes as sample width)
 * @param view Data view to store the end result
 * @param offset offset to start the transcription of `input`
 * @param input the input to convert to 16 bits
 * @see https://webaudiodemos.appspot.com/AudioRecorder/index.html
 * @see https://webaudiodemos.appspot.com/AudioRecorder/js/recorderjs/recorderWorker.js
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common/RiffPcmEncoder.ts#L38
 */
export const floatTo16BitPCM = (
  view: DataView,
  offset: number,
  input: Float32Array
): DataView => {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return view;
};

/**
 *  Write a string in a DataView
 * @param view  DataView to store the results
 * @param offset where to write the string
 * @param string the string to be written
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common/RiffPcmEncoder.ts#L32
 * @see https://webaudiodemos.appspot.com/AudioRecorder/js/recorderjs/recorderWorker.js
 */
export const writeString = (
  view: DataView,
  offset: number,
  string: string
): DataView => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
  return view;
};

/**
 * Prepend the RIFF headers to build a WAV file. The DataView is assumed to be large
 * enough to store the WAV data AND the headers
 *
 * @param view assumed to have the size of futured encoded wav file + 44 bits for the header
 * @param samples raw audio input to get the total file length
 * @param sampleRate default to 16 000 Hz
 * @param bitsPerSample default to 16 bit
 * @param channelsCount default to 1 (mono)
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/sdk/Audio/AudioStreamFormat.ts#L72
 */
export const prependWavHeader = (
  view: DataView,
  samples: Float32Array,
  sampleRate = 16000,
  bitsPerSample = 16,
  channelsCount = 1
) => {
  /* RIFF identifier */
  writeString(view, 0, "RIFF");
  /* file length */
  view.setUint32(4, 32 + samples.length * 2, true);
  /* RIFF type */
  writeString(view, 8, "WAVE");
  /* format chunk identifier */
  writeString(view, 12, "fmt ");
  /* format chunk length */
  view.setUint32(16, 16, true);
  /* sample format (raw) */
  view.setUint16(20, 1, true);
  /* channel count */
  view.setUint16(22, channelsCount, true);
  /* sample rate */
  view.setUint32(24, sampleRate, true);
  /* byte rate (sample rate * block align) */
  const avgRate = sampleRate * channelsCount * (bitsPerSample / 8);
  view.setUint32(28, avgRate, true);
  /* block align (channel count * bytes per sample) */
  view.setUint16(32, (channelsCount * bitsPerSample) / 8, true);
  /* bits per sample */
  view.setUint16(34, bitsPerSample, true);
  /* data chunk identifier */
  writeString(view, 36, "data");
  /* data chunk length */
  view.setUint32(40, samples.length * 2, true);

  return view;
};

/**
 * Transform a raw audio input:
 *  - Downsample to the target rate
 *  - Transform to 16 bits
 *
 * @param rawInput  assumed to be mono-channel audio
 * @param sourceSampleRate sample rate in Hz of the rawInput
 * @param targetSampleRate target sample rate in Hz
 * @param toWav if true, prepend Wav header
 * @see https://github.com/microsoft/cognitive-services-speech-sdk-js/blob/master/src/common/RiffPcmEncoder.ts
 */
export const encodeRawData = (
  rawInput: Float32Array,
  sourceSampleRate: number,
  targetSampleRate: number,
  toWav: boolean
): DataView => {
  // Max sample rate must be target sample rate
  const downsampledFrames = downSample(
    rawInput,
    sourceSampleRate,
    targetSampleRate
  );

  if (!downsampledFrames) {
    throw new Error("Error in encodeRawData when downsampling");
  }

  const audioLength = (toWav ? 44 : 0) + downsampledFrames.length * 2;
  const buffer = new ArrayBuffer(audioLength);
  const view = new DataView(buffer);

  // Optional WAV headers
  if (toWav) {
    prependWavHeader(view, downsampledFrames, targetSampleRate);
  }

  // floatTo16BitPCM
  const offset = toWav ? 44 : 0;
  floatTo16BitPCM(view, offset, downsampledFrames);

  return view;
};

/**
 * Append array2 after array1
 * @param array1 first array, can be empty
 * @param array2 second array, should not be empty
 * @returns [...array1, ...array2]
 */
export const concatenateFloat32Array = (
  array1: Float32Array,
  array2: Float32Array
): Float32Array => {
  // Array 1 is empty
  if (array1.length === 0) {
    return array2;
  }

  // Append data
  // https://stackoverflow.com/a/14071518/4906586
  const newArray = new Float32Array(array1.length + array2.length);
  newArray.set(array1);
  newArray.set(array2, array1.length);

  return newArray;
};
